import { TZDate } from '@repo/tzdate';
import { RefObject, useCallback } from 'react';
import { createAddProductsToCartEvent } from 'src/state/cart/cart.reducer';
import { cartAtom } from 'src/state/cart/cartAtom';
import { showBasketAtom } from 'src/state/ui/showBasket.atom';
import { toggleBookingAtom } from 'src/state/ui/toggleBooking.atom';
import {
    Package,
    PackageTicketOption,
    PackageTicketOptionWithQuantity,
    ProductInstance,
} from '@repo/types';
import { useBookingContext } from 'src/widgets/BookingContext';
import { setAttemptedBooking, setQuantities } from 'src/state/booking/bookingReducer';
import { routeToAppropriateCheckout } from 'src/utils/widget/checkoutRouting';

export function useOnClickBookPackage(
    pkg: Package | null,
    packagePrices: PackageTicketOption[],
    selectedDate: TZDate | null,
    setSelectedDate: (value: TZDate | null) => void,
) {
    const {
        attemptedBooking,
        shouldShowBasketOnBook,
        boxRef,
        hasChosenDate,
        quantities,
        bookingDispatch,
    } = useBookingContext<PackageTicketOptionWithQuantity>();

    const onClickBook = useCallback(
        (selectedProducts: ProductInstance[] = []) => {
            const fn = async () => {
                if (!attemptedBooking) bookingDispatch(setAttemptedBooking(true));

                if (
                    !hasChosenDate ||
                    !pkg ||
                    !selectedDate ||
                    !quantities.some((q) => q.quantity > 0)
                ) {
                    return;
                }

                await cartAtom.update(
                    createAddProductsToCartEvent(
                        selectedProducts,
                        quantities,
                        true, // Packages is not supported by membership api, so this item will disable payment plans
                        {
                            pkg,
                            date: selectedDate,
                        },
                    ),
                );

                if (shouldShowBasketOnBook) {
                    showBasketAtom.update({
                        visible: true,
                        refocusElementOnClose: boxRef.current
                            ? (boxRef as RefObject<HTMLDivElement | null>)
                            : undefined,
                    });
                }

                toggleBookingAtom.update({ visible: false });
                setSelectedDate(null);
                bookingDispatch(setQuantities(updateQuantityData(packagePrices, quantities)));

                if (!shouldShowBasketOnBook) routeToAppropriateCheckout();
            };
            fn();
        },
        [
            pkg,
            attemptedBooking,
            hasChosenDate,
            bookingDispatch,
            quantities,
            boxRef,
            packagePrices,
            shouldShowBasketOnBook,
            selectedDate,
            setSelectedDate,
        ],
    );

    return onClickBook;
}

function updateQuantityData(
    defaultPrices: PackageTicketOption[],
    quantities: PackageTicketOptionWithQuantity[],
): PackageTicketOptionWithQuantity[] {
    return defaultPrices.map((price) => {
        const existingQuantity = quantities.find(
            (quantity) => quantity.ticketCategoryId === price.ticketCategoryId,
        );

        return {
            ...price,
            quantity: existingQuantity?.quantity ?? 0,
        };
    });
}
